import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SaveState } from '@/shared/components/ui/form';

import type { ShareAdapter, ShareSubject } from '@/shared/sharing/types';

interface ShareConfig {
  subject: ShareSubject;
  adapter: ShareAdapter;
}

export interface HistoryTaskItem {
  id: number | string;
  label: string;
}

export interface EditorHistory {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  undoMany?: ((count: number) => void) | undefined;
  redoMany?: ((count: number) => void) | undefined;
  undoTasks?: readonly HistoryTaskItem[] | undefined;
  redoTasks?: readonly HistoryTaskItem[] | undefined;
}

/** A page's Refresh action, which the TopBar shows on phones beside undo. */
export interface TopBarRefresh {
  run: () => void;
  busy: boolean;
  title: string;
}

interface EditorContextValue {
  readonly saveState: SaveState;
  readonly customStatus: string | null;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  /** Whether the shell TopBar should render its history controls. */
  readonly showTopBarHistory: boolean;
  readonly shareConfig: ShareConfig | null;
  readonly setSaveState: (state: SaveState) => void;
  readonly setCustomStatus: (status: string | null) => void;
  readonly setShareConfig: (config: ShareConfig | null) => void;
  readonly setHistory: (history: EditorHistory) => void;
  /** Temporarily hides shell history controls; returns an idempotent cleanup. */
  readonly suppressTopBarHistory: () => () => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly undoMany?: ((count: number) => void) | undefined;
  readonly redoMany?: ((count: number) => void) | undefined;
  readonly undoTasks?: readonly HistoryTaskItem[] | undefined;
  readonly redoTasks?: readonly HistoryTaskItem[] | undefined;
  readonly topBarRefresh: TopBarRefresh | null;
  readonly setTopBarRefresh: (refresh: TopBarRefresh | null) => void;
}

const EditorContext = createContext<EditorContextValue | null>(null);

export function EditorProvider({ children }: { children: ReactNode }) {
  const [saveState, setSaveState] = useState<SaveState>('clean');
  const [customStatus, setCustomStatus] = useState<string | null>(null);
  const [shareConfig, setShareConfig] = useState<ShareConfig | null>(null);
  const [history, setHistoryState] = useState<EditorHistory>({ canUndo: false, canRedo: false, undo: () => undefined, redo: () => undefined });
  const [topBarHistorySuppressions, setTopBarHistorySuppressions] = useState(0);
  const [topBarRefresh, setTopBarRefresh] = useState<TopBarRefresh | null>(null);
  const setHistory = useCallback((next: EditorHistory) => {
    setHistoryState((current) => (
      current.canUndo === next.canUndo &&
      current.canRedo === next.canRedo &&
      current.undo === next.undo &&
      current.redo === next.redo &&
      current.undoMany === next.undoMany &&
      current.redoMany === next.redoMany &&
      current.undoTasks === next.undoTasks &&
      current.redoTasks === next.redoTasks
        ? current
        : next
    ));
  }, []);
  const suppressTopBarHistory = useCallback(() => {
    setTopBarHistorySuppressions((count) => count + 1);
    let restored = false;
    return () => {
      if (restored) return;
      restored = true;
      setTopBarHistorySuppressions((count) => Math.max(0, count - 1));
    };
  }, []);
  // Ctrl+Z / Ctrl+Y (and Ctrl+Shift+Z) run the same history as the undo and redo buttons.
  // While typing in a field the browser's own text undo wins.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !(event.ctrlKey || event.metaKey) || event.altKey) return;
      const key = event.key.toLowerCase();
      const redo = key === 'y' || (key === 'z' && event.shiftKey);
      if (key !== 'z' && key !== 'y') return;
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) && (target as HTMLInputElement).type !== 'checkbox'))) return;
      if (redo ? !history.canRedo : !history.canUndo) return;
      event.preventDefault();
      if (redo) history.redo(); else history.undo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [history]);
  const value = useMemo<EditorContextValue>(() => ({
    saveState,
    customStatus,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    showTopBarHistory: topBarHistorySuppressions === 0,
    shareConfig,
    setSaveState,
    setCustomStatus,
    setShareConfig,
    setHistory,
    suppressTopBarHistory,
    undo: history.undo,
    redo: history.redo,
    undoMany: history.undoMany,
    redoMany: history.redoMany,
    undoTasks: history.undoTasks,
    redoTasks: history.redoTasks,
    topBarRefresh,
    setTopBarRefresh,
  }), [history, saveState, customStatus, shareConfig, setHistory, suppressTopBarHistory, topBarHistorySuppressions, topBarRefresh]);
  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

export function useEditor(): EditorContextValue {
  const context = useContext(EditorContext);
  if (!context) throw new Error('useEditor must be used inside <EditorProvider>');
  return context;
}

/** Optional bridge for shared controls that can render outside the app editor. */
export function useOptionalEditor(): EditorContextValue | null {
  return useContext(EditorContext);
}
